// PerformanceMathSizingDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored (InterviewGamePlanDetail UI style)
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* ignore */
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <h3 className="h6 text-uppercase text-muted mb-0">Sponsored</h3>
          <span className="badge rounded-pill text-bg-light border text-muted">Ads</span>
        </div>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small helpers (same UI style) ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <h2 className="h4 mb-2 fw-bold">{title}</h2>
    </div>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

const Say = ({ children }) => (
  <div className="alert alert-primary d-flex align-items-start gap-2 py-2 mb-3" role="note">
    <span className="badge text-bg-primary">Say</span>
    <div className="mb-0">{children}</div>
  </div>
);

const TagPill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border text-muted fw-semibold">{children}</span>
);

const Panel = ({ title, tag, children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div className="fw-semibold">{title}</div>
        {tag ? <TagPill>{tag}</TagPill> : null}
      </div>
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mb-0">
    {items.map((it, idx) => (
      <li key={idx} className={idx === items.length - 1 ? "" : "mb-3"}>
        <div>{it.main}</div>
        {it.detail ? <div className="medium text-muted mt-1">{it.detail}</div> : null}
      </li>
    ))}
  </ul>
);

const MonoBlock = ({ children }) => (
  <pre className="bg-light border rounded-3 p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
    <code>{children}</code>
  </pre>
);

const TOC = [
  { id: "overview", label: "0) How to use performance math" },
  { id: "latency-throughput", label: "A) Latency & throughput; p50/p95/p99" },
  { id: "littles-law", label: "B) Little’s Law, utilization & queues" },
  { id: "estimation", label: "C) QPS, fan-out, payload, cache hit" },
  { id: "headroom-bursts", label: "D) Headroom, diurnal, bursts" },
  { id: "costing", label: "E) Cost rough-cut" },
  { id: "worked-example", label: "F) Mini worked example (Home Feed)" },
  { id: "quick-formulas", label: "G) Quick formulas & rules" },
];

/* ================== main page ================== */
export default function PerformanceMathSizingDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Running example assumptions (Facebook-like Home Feed). Reasonable interview numbers.
  const ex = useMemo(() => {
    const DAU = 10_000_000; // 10M
    const feedOpensPerUserPerDay = 10;
    const feedReadsPerDay = DAU * feedOpensPerUserPerDay; // 100M/day
    const avgRps = feedReadsPerDay / 86400; // ~1157
    const peakMultiplier = 8;
    const peakRps = avgRps * peakMultiplier; // ~9256

    const p95SloMs = 250;

    const pageItems = 25;
    const itemSummaryBytes = 800; // IDs + minimal metadata (NOT media)
    const pageBytes = pageItems * itemSummaryBytes; // ~20KB
    const pageKb = pageBytes / 1024;

    const cacheHit = 0.9;
    const dbQps = peakRps * (1 - cacheHit);

    // For “egress intuition” (metadata only)
    const peakMetadataEgressMBps = (peakRps * pageBytes) / (1024 * 1024);

    return {
      DAU,
      feedOpensPerUserPerDay,
      feedReadsPerDay,
      avgRps,
      peakMultiplier,
      peakRps,
      p95SloMs,
      pageItems,
      itemSummaryBytes,
      pageBytes,
      pageKb,
      cacheHit,
      dbQps,
      peakMetadataEgressMBps,
    };
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Performance math &amp; sizing</span>
      </nav>

      {/* header (match InterviewGamePlanDetail) */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(25,135,84,.08), rgba(13,110,253,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1 fw-bold">2) Performance math &amp; sizing — detailed guide</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 900, margin: "0 auto" }}>
            A reusable sizing template for <em>any</em> system design interview, plus a running example:{" "}
            <strong>Facebook-like Home Feed</strong> (read-heavy feed math, cache hits, and latency budgets).
          </p>
        </div>
      </header>

      {/* content: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT: On this page */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h3 className="h6 text-uppercase text-muted mb-0">On this page</h3>
                  <TagPill>TOC</TagPill>
                </div>

                <nav className="nav flex-column small">
                  {TOC.map(({ id: tocId, label }) => (
                    <a
                      key={tocId}
                      href={`#${tocId}`}
                      className={`nav-link px-0 ${activeId === tocId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === tocId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* 0 */}
          <Section id="overview" title="0) How to use performance math in interviews">
            <Say>
              “I’ll do quick, defensible math to pick the right architecture: latency budget, peak QPS, cache hit target,
              headroom, and a rough cost sketch.”
            </Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Start with 3 anchors:</strong> peak QPS, payload size (p50/p95), and latency SLO (p95/p99).
                      </>
                    ),
                    detail:
                      "Most sizing mistakes come from using averages. Use peak with a multiplier, and speak in percentiles (p95/p99).",
                  },
                  {
                    main: (
                      <>
                        <strong>Convert users → actions → QPS:</strong> DAU × actions/day ÷ 86,400 → avg; then apply peak multiplier.
                      </>
                    ),
                    detail:
                      "If you’re missing numbers, make reasonable assumptions and say them out loud. The reasoning matters more than precision.",
                  },
                  {
                    main: (
                      <>
                        <strong>Use Little’s Law:</strong> concurrency = RPS × latency (seconds) to size pools and in-flight limits.
                      </>
                    ),
                    detail:
                      "This is the fastest way to estimate how many requests are in flight and whether pools/queues are safe at peak.",
                  },
                  {
                    main: (
                      <>
                        <strong>Turn cache hit into DB load:</strong> origin QPS = QPS × (1 − hit).
                      </>
                    ),
                    detail:
                      "A 90% cache hit reduces DB by 10×. This single line often justifies introducing Redis/Memcached.",
                  },
                  {
                    main: (
                      <>
                        <strong>Always include headroom:</strong> size for peak × (1.3–2.0) and keep utilization under the knee.
                      </>
                    ),
                    detail:
                      "As utilization approaches 1, queueing delay explodes. Keep ρ ≤ ~0.6–0.7 for latency-sensitive paths.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — baseline assumptions" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>DAU:</strong> {(ex.DAU / 1e6).toFixed(0)}M; <strong>feed opens/user/day:</strong> {ex.feedOpensPerUserPerDay}
                        {" → "}
                        {(ex.feedReadsPerDay / 1e6).toFixed(0)}M feed reads/day
                      </>
                    ),
                    detail: `Avg ≈ ${Math.round(ex.avgRps).toLocaleString()} RPS; peak (×${ex.peakMultiplier}) ≈ ${Math.round(
                      ex.peakRps
                    ).toLocaleString()} RPS.`,
                  },
                  {
                    main: (
                      <>
                        <strong>SLO:</strong> GET /feed p95 ≤ {ex.p95SloMs}ms (server-side)
                      </>
                    ),
                    detail: "We will budget latency hop-by-hop and keep retries/hedging within this envelope.",
                  },
                  {
                    main: (
                      <>
                        <strong>Payload:</strong> {ex.pageItems} items/page, ~{ex.itemSummaryBytes}B/item → ~{ex.pageKb.toFixed(0)}KB/page (metadata only)
                      </>
                    ),
                    detail: "Media bytes are served via CDN/object store; the feed API returns metadata + URLs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Cache hit target:</strong> {(ex.cacheHit * 100).toFixed(0)}% for the feed head
                      </>
                    ),
                    detail: `DB sees ~${Math.round(ex.dbQps).toLocaleString()} QPS at peak on misses.`,
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* A */}
          <Section id="latency-throughput" title="A) Latency & throughput basics; p50 / p95 / p99 thinking">
            <Say>
              “Latency is per-request time; throughput is requests per second. I’ll reason in percentiles and set a
              hop-by-hop budget.”
            </Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Define SLO in percentiles:</strong> p95/p99 latency target + error/availability target for the critical endpoint.
                      </>
                    ),
                    detail:
                      "Percentiles matter because tail latency drives user pain. Pick the handful of endpoints that matter most.",
                  },
                  {
                    main: (
                      <>
                        <strong>Budget hop-by-hop:</strong> LB/edge → auth → service → cache → DB → serdes/network.
                      </>
                    ),
                    detail:
                      "Always keep a buffer for tail risk. If you spend your entire budget on ‘expected’ work, p95 will miss.",
                  },
                  {
                    main: (
                      <>
                        <strong>Tail amplifies with fan-out:</strong> overall latency trends toward the slowest subcall.
                      </>
                    ),
                    detail:
                      "This is why we batch and parallelize carefully, and why we cap fan-out in the read path.",
                  },
                  {
                    main: (
                      <>
                        <strong>Tail mitigation:</strong> per-hop timeouts, bounded retries + jitter, hedged reads for idempotent reads, bulkheads.
                      </>
                    ),
                    detail:
                      "Retries can worsen tail if unbounded. Hedging is a controlled way to cut p99 when replicas are independent.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed — SLO & budget (example)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>SLO:</strong> GET /feed p95 ≤ {ex.p95SloMs}ms (server)
                      </>
                    ),
                    detail:
                      "We keep the read path fast using cache hits; slow dependencies trigger fallback to last-good cached pages.",
                  },
                  {
                    main: (
                      <>
                        <strong>Budget split:</strong> Edge/LB 10ms + Auth 15ms + FeedReadSvc 15ms + Redis 10ms + DB 50ms + serdes/net 25ms + buffer 125ms = 250ms
                      </>
                    ),
                    detail:
                      "If DB p95 rises, the buffer gets eaten first. That’s your early signal to scale cache, reduce misses, or shard.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Optional: speakable ‘budget line’" tag="Example">
              <MonoBlock>{`“We’ll target p95 250ms for feed reads. I’ll budget it across hops and keep ~50% as buffer for tail risk, so we can survive spikes without missing SLO.”`}</MonoBlock>
            </Panel>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}

          {/* B */}
          <Section id="littles-law" title="B) Little’s Law (L = λW), utilization, and queueing intuition">
            <Say>“I’ll estimate concurrency with Little’s Law and keep utilization below the knee so tails don’t explode.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Little’s Law:</strong> <code>L = λ × W</code> — in-flight concurrency = RPS × avg time in system.
                      </>
                    ),
                    detail:
                      "Use this to size thread pools, connection pools, and max in-flight requests at peak.",
                  },
                  {
                    main: (
                      <>
                        <strong>Utilization:</strong> <code>ρ = λ / μ</code>. As ρ → 1, queueing delay grows superlinearly.
                      </>
                    ),
                    detail:
                      "You don’t need exact queueing math—just explain the knee and why headroom is mandatory.",
                  },
                  {
                    main: (
                      <>
                        <strong>Rule of thumb:</strong> keep ρ ≤ 0.6–0.7 at peak for latency-sensitive services.
                      </>
                    ),
                    detail:
                      "Higher utilization may be fine for offline/batch; it’s dangerous for interactive APIs like feeds.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed — concurrency & knee (example)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Concurrency:</strong> peak λ≈{Math.round(ex.peakRps).toLocaleString()} RPS and W≈120ms (0.12s) → L≈{" "}
                        {Math.round(ex.peakRps * 0.12).toLocaleString()} in-flight
                      </>
                    ),
                    detail:
                      "That’s the number that should guide max in-flight limits and pool sizing. If your pools are smaller, you’ll see queueing and p95 spikes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Knee intuition:</strong> If a DB node sustains μ=2000 QPS at S=50ms, then ρ=0.6 is healthy; ρ=0.85 produces big tail latency.
                      </>
                    ),
                    detail:
                      "This is why feed systems keep DB protected with caches and avoid saturating it during traffic bursts.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* C */}
          <Section id="estimation" title="C) Estimate: read/write QPS, fan-out, payload sizes, cache hit ratio">
            <Say>“I’ll compute peak QPS, then apply cache-hit and fan-out to see the real load on DB, egress, and workers.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Traffic sizing:</strong> DAU/MAU × actions/user → avg RPS; apply peak multiplier (×4–×10).
                      </>
                    ),
                    detail:
                      "Use peak to select storage/caches; use average mainly for cost intuition.",
                  },
                  {
                    main: (
                      <>
                        <strong>Fan-out math:</strong> backend QPS = front-door QPS × fan-out × (1 − cache_hit) per layer.
                      </>
                    ),
                    detail:
                      "Identify where fan-out happens (sync read path vs async pipelines) and ensure the read path stays bounded.",
                  },
                  {
                    main: (
                      <>
                        <strong>Payload sizing:</strong> use p50/p95 sizes; use pagination, compression, and CDNs.
                      </>
                    ),
                    detail:
                      "Separate metadata payload from media bytes. Many feed systems return IDs + minimal metadata and hydrate in batch.",
                  },
                  {
                    main: (
                      <>
                        <strong>Cache hit target H:</strong> origin QPS = QPS × (1 − H); prevent stampede via coalescing + jittered TTL.
                      </>
                    ),
                    detail:
                      "Cache hit drives DB cost and latency. Mention stale-while-revalidate and request coalescing for hot keys.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed — peak QPS, DB load, and egress (example)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Peak feed reads:</strong> ~{Math.round(ex.peakRps).toLocaleString()} RPS
                      </>
                    ),
                    detail: `Derived from DAU and opens/day. Peak multiplier accounts for diurnal + bursts.`,
                  },
                  {
                    main: (
                      <>
                        <strong>Cache hit:</strong> {(ex.cacheHit * 100).toFixed(0)}% → DB sees ~{Math.round(ex.dbQps).toLocaleString()} QPS
                      </>
                    ),
                    detail:
                      "This single line shows why Redis matters: 90% hit makes DB 10× smaller and keeps tails under control.",
                  },
                  {
                    main: (
                      <>
                        <strong>Payload:</strong> ~{ex.pageKb.toFixed(0)}KB per page (metadata only) → peak metadata egress ~{ex.peakMetadataEgressMBps.toFixed(0)} MB/s
                      </>
                    ),
                    detail:
                      "Real egress is dominated by media bytes, so use CDN for images/videos and keep API responses small and cacheable where possible.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hydration:</strong> batch-get posts by postIds to avoid N+1 calls
                      </>
                    ),
                    detail:
                      "Even if each page has 25 items, don’t do 25 RPCs. Do 1–2 batch calls and cache hydrated post summaries.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* D */}
          <Section id="headroom-bursts" title="D) Capacity headroom, peak/diurnal traffic, burst handling">
            <Say>“I’ll size for peak with headroom and describe the overload behavior (rate-limit, queue, shed).”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Headroom policy:</strong> size for peak × 1.3–2.0; keep ρ ≤ 60–70% at peak.
                      </>
                    ),
                    detail:
                      "Headroom covers failure (AZ loss), deployments, and randomness in tail latency.",
                  },
                  {
                    main: (
                      <>
                        <strong>Bursts:</strong> queues between tiers; autoscale on lag/latency; shed non-critical work; early 429/503 with Retry-After.
                      </>
                    ),
                    detail:
                      "Prefer fast failures over slow timeouts. Protect DB first with pool caps, backpressure, and circuit breakers.",
                  },
                  {
                    main: (
                      <>
                        <strong>Diurnal:</strong> run batch/compaction off-peak; plan for event spikes.
                      </>
                    ),
                    detail:
                      "If the workload has a daily peak, schedule heavy background jobs away from it.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed — quick instance sizing sketch (example)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Provisioned target:</strong> peak × 1.5 ={" "}
                        {Math.round(ex.peakRps * 1.5).toLocaleString()} RPS
                      </>
                    ),
                    detail:
                      "This includes headroom for spikes and partial failure.",
                  },
                  {
                    main: (
                      <>
                        <strong>If one API instance sustains:</strong> 400 RPS at ~60% utilization → need ~
                        {Math.ceil((ex.peakRps * 1.5) / 400).toLocaleString()} instances
                      </>
                    ),
                    detail:
                      "Then add N+1/AZ and room for deploys. Exact numbers depend on CPU cost of ranking/hydration.",
                  },
                  {
                    main: (
                      <>
                        <strong>Burst plan:</strong> edge token buckets + brownout mode (fall back to cached/recency feed), autoscale workers on queue lag.
                      </>
                    ),
                    detail:
                      "If ranking pipeline is unhealthy, degrade to recency to keep availability and latency SLOs.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* E */}
          <Section id="costing" title="E) Cost rough-cut (compute, egress, storage, ops)">
            <Say>“I’ll sketch costs at a high level and call out the biggest levers.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Compute:</strong> instances = count × $/hr × 730 (include spare/LB/NAT). Serverless = requests + duration.
                      </>
                    ),
                    detail:
                      "Give relative drivers rather than exact dollars. Mention you’ll validate with real pricing later.",
                  },
                  {
                    main: (
                      <>
                        <strong>Egress:</strong> GB egressed × $/GB; CDN can reduce origin egress dramatically.
                      </>
                    ),
                    detail:
                      "For feeds, media egress usually dominates. CDN caching and compression are the big levers.",
                  },
                  {
                    main: (
                      <>
                        <strong>Storage:</strong> GB-month across hot/warm/cold tiers; include IO/requests; lifecycle old data.
                      </>
                    ),
                    detail:
                      "Feeds and posts grow fast. Use TTL/lifecycle and keep indexes lean.",
                  },
                  {
                    main: (
                      <>
                        <strong>Ops/observability:</strong> logs/metrics/traces (GB/day), retention policies, sampling.
                      </>
                    ),
                    detail:
                      "Sampling and retention are cost controls. Also avoid logging PII and large payloads.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed — cost levers (example)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Biggest costs:</strong> CDN/media egress, cache tier, and DB reads (if cache hit is low).
                      </>
                    ),
                    detail:
                      "Keeping cache hit high reduces both DB cost and tail latency.",
                  },
                  {
                    main: (
                      <>
                        <strong>Largest savings:</strong> smaller payloads, better cache hit, aggressive CDN for media, and log sampling.
                      </>
                    ),
                    detail:
                      "Return metadata + URLs (not media). Compress responses. Use lifecycle policies for older content and logs.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Quick line items you can say" tag="Template">
              <MonoBlock>{`Compute ≈ instance_count × $/hr × 730
Storage ≈ GB × $/GB-month (+ IO/requests)
Egress ≈ GB_out × $/GB (CDN reduces origin)
Observability ≈ GB/day × $/GB (retention + sampling matters)`}</MonoBlock>
            </Panel>
          </Section>

          {/* F */}
          <Section id="worked-example" title="F) Mini worked example (tie it all together)">
            <Say>“Now I’ll run the full template end-to-end using the Home Feed assumptions.”</Say>

            <Panel title="Home Feed — end-to-end sizing narrative" tag="Example">
              <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                {`Problem: Read-heavy Facebook-like Home Feed (metadata response; media via CDN).
Inputs:
- DAU: ${(ex.DAU / 1e6).toFixed(0)}M
- Feed opens/user/day: ${ex.feedOpensPerUserPerDay}
- Reads/day: ${(ex.feedReadsPerDay / 1e6).toFixed(0)}M → avg ~${Math.round(ex.avgRps).toLocaleString()} RPS
- Peak multiplier: ×${ex.peakMultiplier} → peak ~${Math.round(ex.peakRps).toLocaleString()} RPS
- SLO: feed read p95 ≤ ${ex.p95SloMs} ms
- Cache hit target: ${(ex.cacheHit * 100).toFixed(0)}%
- Feed skeleton payload: ~${ex.pageKb.toFixed(0)}KB/page

1) Latency budget
Edge/LB 10, Auth 15, Service 15, Redis 10, DB 50, net/serdes 25, buffer 125 → 250 ms.
Hedged reads after ~120 ms to a second replica (idempotent reads only).

2) Concurrency (Little’s Law)
At peak λ=${Math.round(ex.peakRps).toLocaleString()} RPS and W≈0.12s:
L ≈ ${Math.round(ex.peakRps * 0.12).toLocaleString()} in-flight.
Make sure pools, max-in-flight, and autoscale policies cover this with headroom.

3) Cache → DB protection
DB QPS ≈ peak × (1−H) = ${Math.round(ex.peakRps).toLocaleString()} × ${(1 - ex.cacheHit).toFixed(
                  2
                )} ≈ ${Math.round(ex.dbQps).toLocaleString()} QPS.
Protect hot keys with request coalescing + staggered TTLs.

4) API fleet sizing (rough)
Provision target: peak × 1.5 = ${Math.round(ex.peakRps * 1.5).toLocaleString()} RPS.
If one instance safely handles 400 RPS at target utilization:
Need ≈ ${Math.ceil((ex.peakRps * 1.5) / 400).toLocaleString()} instances + N+1/AZ + deploy room.

5) Egress intuition (metadata only)
Peak metadata egress ≈ ${ex.peakMetadataEgressMBps.toFixed(0)} MB/s.
Media egress is much bigger → CDN is mandatory.

6) Burst plan
Edge rate limits + autoscale on latency/queue lag.
Brownout: if ranking is slow, serve cached/recency feed.
Protect DB with pool caps and circuit breakers (fast fail over slow timeouts).`}
              </div>
            </Panel>
          </Section>

          {/* G */}
          <Section id="quick-formulas" title="G) Quick formulas & rules you can quote">
            <Say>“Here are the short formulas you can quote during sizing.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  { main: <><strong>Little’s Law:</strong> <code>L = λW</code> (concurrency = RPS × latency)</>, detail: "Use for pools, in-flight, worker counts." },
                  { main: <><strong>Utilization:</strong> <code>ρ = λ/μ</code>; keep ≤ 0.6–0.7 at peak</>, detail: "Avoid the knee where queues explode and tails spike." },
                  { main: <><strong>M/M/1 intuition:</strong> <code>R ≈ S / (1 − ρ)</code></>, detail: "Not exact in real systems, but great for explaining why headroom matters." },
                  { main: <><strong>Cache:</strong> <code>QPS_origin = QPS × (1 − H)</code></>, detail: "Hit rate is the biggest lever for DB load and tail latency." },
                  { main: <><strong>Peak planning:</strong> <code>peak = avg × multiplier</code> (×4–×10)</>, detail: "Use peak for sizing, avg for cost intuition." },
                  { main: <><strong>Retry budget:</strong> retries must fit inside SLO</>, detail: "Use bounded retries + jitter; idempotency keys for writes." },
                ]}
              />
            </Panel>

            <Panel title="Speakable mini-cheat sheet" tag="Template">
              <MonoBlock>{`avg_RPS = actions_per_day / 86,400
peak_RPS = avg_RPS × peak_multiplier
concurrency ≈ peak_RPS × latency_seconds
origin_QPS = peak_RPS × (1 − cache_hit)
keep utilization below ~70% at peak (tails explode near saturation)`}</MonoBlock>
            </Panel>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
